import { useNavigate } from 'react-router';
import { CreateCaseForm } from '@podus/ui';
import type { CaseFormData, PartyEntry } from '@podus/ui';
import { api } from '@/lib/api';
import { podusApi } from '@/lib/podusApi';
import { useMultidoorState } from '@/context/StateContext';
import type { Case } from '@/types';

function cleanParty(p: PartyEntry, role: 'Applicant' | 'Respondent', partyType: string | undefined) {
    const out: Record<string, unknown> = { name: p.name?.trim(), role, partyType };
    if (p.address?.trim()) out.address = p.address.trim();
    if (p.phone?.trim()) out.phone = p.phone.trim();
    if (p.email?.trim()) out.email = p.email.trim();
    return out;
}

interface InitiatePaymentResponse {
    txRef: string;
    checkoutUrl: string;
    intentId: number;
}

export default function UserCreateCase() {
    const navigate = useNavigate();
    const { selectedState } = useMultidoorState();

    const handleSubmit = async (_caseType: string, formData: CaseFormData) => {
        // 1. Create the case
        const res = await api.post<Case>('/api/cases/online', {
            disputeCategory: formData.disputeCategory,
            description: formData.description?.trim() || undefined,
            parties: [
                ...(formData.applicants || []).map((p) => cleanParty(p, 'Applicant', formData.applicantType)),
                ...(formData.respondents || []).map((p) => cleanParty(p, 'Respondent', formData.respondentType)),
            ],
        });

        const createdCase = res.response.data;
        if (!createdCase?.id) return;

        // 2. Upload evidence files (if any)
        if (formData.files.length > 0) {
            const uploadable = formData.files.filter((f) => f.file);
            if (uploadable.length > 0) {
                const fd = new FormData();
                uploadable.forEach((f) => fd.append('files', f.file!));
                const docNames = Array.from(formData.uploadedDocs || []);
                docNames.forEach((name) => fd.append('mandatoryDocTypes', name));
                for (let i = docNames.length; i < uploadable.length; i++) {
                    fd.append('mandatoryDocTypes', '');
                }
                try {
                    await api.upload(`/api/cases/${createdCase.id}/evidence`, fd);
                } catch (err) {
                    console.error('Evidence upload failed:', err);
                }
            }
        }

        // 3. Kick off a Flutterwave checkout via NEW_PODUS_API
        const applicant = formData.applicants?.[0];
        const redirectUrl = `${window.location.origin}/dashboard?payment=success&case=${createdCase.id}`;
        const payment = await podusApi.post<InitiatePaymentResponse>('/payments/initiate/flutterwave', {
            product: 'multidoor',
            productRef: String(createdCase.id),
            stateSlug: selectedState?.slug,
            amount: Number(createdCase.amountPaid) || 10000,
            currency: 'NGN',
            customer: {
                email: applicant?.email || 'noreply@podus.org',
                name: applicant?.name,
                phone: applicant?.phone,
            },
            redirectUrl,
            metadata: {
                caseNumber: createdCase.caseNumber,
                disputeCategory: formData.disputeCategory,
            },
        });

        // 4. Redirect the browser to the hosted checkout page
        if (payment?.checkoutUrl) {
            window.location.href = payment.checkoutUrl;
        }
    };

    return (
        <div className="bg-white rounded-lg shadow p-6 pb-0 max-w-4xl mx-auto">
            <CreateCaseForm
                caseType="online"
                title="Create Multi-door Case"
                subtitle="Let’s start with your details. This helps the court know who is bringing the dispute and how to reach you."
                showTerms
                hidePaymentMethod
                onSubmit={handleSubmit}
                onSuccess={() => navigate('/dashboard')}
            />
        </div>
    );
}
